/**
 * PostHog browser client for the storefront / seller web app.
 *
 * Init is lazy and guarded: with no NEXT_PUBLIC_POSTHOG_KEY the module stays a
 * no-op, so the app behaves exactly as it did before the key exists. Every
 * export is safe to call during SSR.
 *
 * Provider selection still goes through utils/analyticsConfig.js — this module
 * only owns the SDK lifecycle.
 */

import posthog from "posthog-js";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";

// Development sends nothing unless you opt in explicitly, so local work does not
// pollute the production project's funnels or burn free-tier event quota.
const ENABLED_IN_DEV = process.env.NEXT_PUBLIC_POSTHOG_DEV_ENABLED === "true";

// The one property that tells the four clients apart (see the `ph.register`
// call below). Exported so utils/analytics.js's resetUser() can re-register
// it after posthog.reset() wipes the persistence layer this lives in.
export const PLATFORM_SUPER_PROPERTY = { platform: "storefront" };

let initialized = false;
let initFailed = false;

/**
 * Whether PostHog is configured and allowed to send in this environment.
 * @returns {boolean}
 */
export const isPostHogEnabled = () => {
  if (typeof window === "undefined") return false;
  if (!POSTHOG_KEY) return false;
  if (process.env.NODE_ENV === "development" && !ENABLED_IN_DEV) return false;
  return !initFailed;
};

/**
 * Initialize the PostHog browser SDK. Idempotent; safe to call on every mount.
 * @returns {boolean} whether the client is usable after this call
 */
export const initPostHog = () => {
  if (initialized) return !initFailed;
  if (!isPostHogEnabled()) return false;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: "https://us.posthog.com",

      // The storefront is mostly anonymous buyers. Anonymous events are far
      // cheaper than person profiles, so only create a profile once we actually
      // call identify() for a logged-in seller or a placed order.
      person_profiles: "identified_only",

      // Page views are sent by hand from _app.js: this is a Next 12 pages-router
      // SPA, so the SDK's own listener would miss client-side route changes.
      capture_pageview: false,
      capture_pageleave: true,

      autocapture: true,
      rageclick: true,

      // network_timing puts "GET /storefront/... 200" on the session timeline
      // beside the clicks — the equivalent of Sentry's XHR and Fetch
      // breadcrumbs. It reads the browser's own PerformanceObserver entries,
      // which exist whether or not we ask for them, so it adds no work to the
      // page and no request of its own.
      capture_performance: {
        network_timing: true,
        web_vitals: true,
      },

      // Dormant backstop. Replay is disabled outright below, so none of this
      // runs today — it is kept so that if anyone ever does re-enable recording
      // on the storefront, it cannot start out unmasked. Do not read the
      // presence of this block as evidence that buyers are being recorded.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: "[data-ph-mask]",

        // Headers, not bodies. A checkout request body is a name, a phone
        // number and a delivery address.
        recordHeaders: true,
        recordBody: false,

        maskCapturedNetworkRequestFn: (request) => {
          const masked = { ...request };
          delete masked.requestHeaders?.Authorization;
          delete masked.requestHeaders?.authorization;
          delete masked.requestHeaders?.Cookie;
          delete masked.requestHeaders?.cookie;
          masked.requestBody = null;
          masked.responseBody = null;
          // Query strings on this app carry phone numbers on the order-lookup
          // routes, exactly as the backend's tracing drops them.
          if (typeof masked.name === "string") masked.name = masked.name.split("?")[0];
          return masked;
        },
      },

      // Session replay is OFF on the storefront, permanently and by product
      // decision (owner, 2026-09-17): buyers are not to be recorded.
      //
      // This is deliberately a hard-coded `true` and not a dial. It previously
      // read the `storefront-session-replay` feature flag, which meant anyone
      // with PostHog UI access could start recording buyers by moving a
      // percentage slider — no deploy, no review. That path is now gone:
      // nothing in this file calls `startSessionRecording()`, so neither the
      // flag nor `NEXT_PUBLIC_POSTHOG_RECORDING` can turn replay on here.
      //
      // Admin is a different decision and does still record; see
      // sh0p-admin/src/lib/posthog.ts.
      disable_session_recording: true,

      persistence: "localStorage+cookie",
      loaded: (ph) => {
        // No ph.debug() — floods the buyer console with flag/IndexedDB chatter.

        // The one property that tells the four clients apart.
        //
        // Registered here rather than alongside the store context, so it is on
        // the very first event — a buyer browsing anonymously never reaches the
        // identify() at order confirmation, and those sessions are most of the
        // traffic.
        //
        // iOS and Android already register `platform` ("ios" / "android"); the
        // two web clients are both `$lib: "web"` and were otherwise
        // indistinguishable. Reusing their key means one breakdown splits all
        // four, which is what the mobile-vs-admin funnels in POSTHOG.md assume.
        ph.register(PLATFORM_SUPER_PROPERTY);
      },
    });
    initialized = true;
    return true;
  } catch (error) {
    // A broken analytics SDK must never take the storefront down with it.
    initFailed = true;
    initialized = true;
    console.error(`Failed to initialize PostHog: ${error.message}`);
    return false;
  }
};

/**
 * The raw client, or null when PostHog is not running.
 * Call sites should prefer utils/analytics.js over reaching for this.
 * @returns {import("posthog-js").PostHog | null}
 */
export const getPostHog = () => {
  if (!initialized) initPostHog();
  return initialized && !initFailed ? posthog : null;
};

export default posthog;
